import { useEffect, useState } from 'react';
import { Bot, Check, DownloadCloud, Filter, MessageSquareText, RefreshCw, Search, X } from 'lucide-react';
import { formatDate, get, post, qs } from '../lib/api';
import { Modal } from '../components/Modal';
import { StatusBadge } from '../components/StatusBadge';

const readableTag = (value: string) => value.length <= 80 && !/[\\()[\]{}?*+|]|\(\?/.test(value);

export function MaxInboxPage() {
  const [items, setItems] = useState<any[]>([]);
  const [systems, setSystems] = useState<any[]>([]);
  const [filters, setFilters] = useState({ q: '', review: 'pending', it: '1' });
  const [importForm, setImportForm] = useState<any | null>(null);
  const [approve, setApprove] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => Promise.all([
    get<any[]>('/api/max/messages' + qs(filters)),
    get<any[]>('/api/systems')
  ]).then(([messages, systemRows]) => {
    setItems(messages);
    setSystems(systemRows);
  });

  useEffect(() => { void load(); }, [filters.q, filters.review, filters.it]);

  const runImport = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      const result: any = await post('/api/max/import-history', {
        ...importForm,
        maxPages: Number(importForm.maxPages)
      });
      setMsg(
        `Получено: ${result.received}, добавлено: ${result.saved}, `
        + `дублей: ${result.duplicates}, пропущено служебных: ${result.ignored || 0}`
      );
      await load();
    } catch (error: any) {
      setMsg(error.message);
    } finally {
      setBusy(false);
    }
  };

  const approveMessage = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await post(`/api/max/messages/${approve.id}/approve`, {
        system_id: approve.system_id || null,
        title: approve.title,
        priority: approve.priority
      });
      setApprove(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  return <>
    <section className="section-intro">
      <div>
        <div className="eyebrow">Интеграция с технической группой</div>
        <h2>Заявки из MAX</h2>
        <p>
          Система анализирует основной и пересланный текст. Короткие служебные ответы
          «Принято», «В работе», «Решено», «Исправлено» и аналогичные статусы в очередь не добавляются.
          Инцидент создаётся только после подтверждения.
        </p>
      </div>
      <button className="primary" onClick={() => setImportForm({ chatId: '', dateFrom: '', dateTo: '', maxPages: 100 })}>
        <DownloadCloud size={18}/>Импортировать историю
      </button>
    </section>

    <div className="integration-note">
      <Bot/>
      <div>
        <strong>Безопасный режим</strong>
        <span>
          Токен MAX хранится только в серверном .env. Импорт использует чтение истории,
          а сообщения-статусы и телеметрия автоматически пропускаются.
        </span>
      </div>
    </div>

    {msg && <div className="save-message">{msg}</div>}

    <section className="filter-bar">
      <div className="search-box">
        <Search size={18}/>
        <input
          placeholder="Текст сообщения, отправитель, система…"
          value={filters.q}
          onChange={event => setFilters({ ...filters, q: event.target.value })}
        />
      </div>
      <label>
        <Filter size={16}/>
        <select value={filters.review} onChange={event => setFilters({ ...filters, review: event.target.value })}>
          <option value="">Все решения</option>
          <option value="pending">На проверке</option>
          <option value="approved">Принято</option>
          <option value="rejected">Отклонено</option>
        </select>
      </label>
      <label>
        <select value={filters.it} onChange={event => setFilters({ ...filters, it: event.target.value })}>
          <option value="">Все сообщения</option>
          <option value="1">Вероятно ИТ</option>
          <option value="0">Не ИТ</option>
        </select>
      </label>
      <button className="icon-btn" onClick={() => void load()}><RefreshCw size={18}/></button>
    </section>

    {items.length
      ? <div className="message-list">
          {items.map(message => <article className={`message-card ${message.is_it ? 'is-it' : 'not-it'}`} key={message.id}>
            <div className="message-avatar"><MessageSquareText/></div>
            <div className="message-content">
              <div className="message-head">
                <div>
                  <strong>{message.sender_name || `Пользователь ${message.sender_id || 'MAX'}`}</strong>
                  <span>{formatDate(message.message_time)}</span>
                </div>
                <div>
                  <span className="confidence">{Math.round(message.confidence * 100)}%</span>
                  <StatusBadge value={message.review_status}/>
                </div>
              </div>
              <p>{message.text || 'Сообщение без текста'}</p>
              <div className="message-tags">
                {message.suggested_system_name && <em>{message.suggested_system_name}</em>}
                {message.matched_keywords?.filter(readableTag).slice(0, 10).map((value: string) => <span key={value}>{value}</span>)}
              </div>
            </div>
            {message.review_status === 'pending' && <div className="message-actions">
              <button className="approve-btn" onClick={() => setApprove({
                ...message,
                system_id: message.suggested_system_id || '',
                title: message.text?.slice(0, 180) || 'Заявка из MAX',
                priority: 'p3'
              })}><Check/>Принять</button>
              <button className="reject-btn" onClick={async () => {
                await post(`/api/max/messages/${message.id}/reject`);
                await load();
              }}><X/>Не ИТ</button>
            </div>}
          </article>)}
        </div>
      : <div className="empty"><Bot/><strong>Очередь пуста</strong><span>Запустите импорт истории или подключите получение новых сообщений.</span></div>}

    {importForm && <Modal title="Импорт истории MAX" onClose={() => setImportForm(null)}>
      <form className="form-grid" onSubmit={runImport}>
        <label className="span-2">Chat ID технической группы
          <input required value={importForm.chatId} onChange={event => setImportForm({ ...importForm, chatId: event.target.value })} placeholder="Числовой идентификатор чата"/>
        </label>
        <label>С даты
          <input type="datetime-local" value={importForm.dateFrom} onChange={event => setImportForm({ ...importForm, dateFrom: event.target.value })}/>
        </label>
        <label>По дату
          <input type="datetime-local" value={importForm.dateTo} onChange={event => setImportForm({ ...importForm, dateTo: event.target.value })}/>
        </label>
        <label>Максимум страниц по 100 сообщений
          <input type="number" min="1" max="1000" value={importForm.maxPages} onChange={event => setImportForm({ ...importForm, maxPages: event.target.value })}/>
        </label>
        <div className="hint span-2">
          Повторный импорт безопасен: сообщения дедуплицируются по ID MAX. Короткие ответы-статусы и телеметрия пропускаются.
        </div>
        <div className="modal-actions span-2">
          <button type="button" className="secondary" onClick={() => setImportForm(null)}>Закрыть</button>
          <button className="primary" disabled={busy}>{busy ? 'Импорт…' : 'Начать импорт'}</button>
        </div>
      </form>
    </Modal>}

    {approve && <Modal title="Создать инцидент из сообщения" onClose={() => setApprove(null)}>
      <form className="form-grid" onSubmit={approveMessage}>
        <label className="span-2">Название
          <input required value={approve.title} onChange={event => setApprove({ ...approve, title: event.target.value })}/>
        </label>
        <label className="span-2">Система
          <select value={approve.system_id} onChange={event => setApprove({ ...approve, system_id: event.target.value })}>
            <option value="">Без привязки</option>
            {systems.map(system => <option key={system.id} value={system.id}>{system.code} · {system.name}</option>)}
          </select>
        </label>
        <label>Приоритет
          <select value={approve.priority} onChange={event => setApprove({ ...approve, priority: event.target.value })}>
            <option value="p1">P1 — критический</option>
            <option value="p2">P2 — высокий</option>
            <option value="p3">P3 — средний</option>
            <option value="p4">P4 — низкий</option>
          </select>
        </label>
        <div className="source-message span-2">{approve.text}</div>
        <div className="modal-actions span-2">
          <button type="button" className="secondary" onClick={() => setApprove(null)}>Отмена</button>
          <button className="primary" disabled={busy}>Создать инцидент</button>
        </div>
      </form>
    </Modal>}
  </>;
}
